"use client";

import * as React from "react";
import { Sidebar, SidebarLayout } from "@/src/components/navigation/sidebar";
import { PageHeader, Logo, ScrollArea, Text } from "@/src/components";
import { ComponentPage } from "@/components/docs/component-page";
import { Book, FileText, Settings } from "lucide-react";

// Demo navigation for the sidebar example
const demoNavigation = [
  {
    title: "Documentation",
    subsections: [
      {
        title: "Getting Started",
        items: [
          { label: "Introduction", href: "#", icon: <Book className="h-4 w-4" /> },
          { label: "Installation", href: "#", icon: <FileText className="h-4 w-4" /> },
        ],
      },
      {
        title: "Settings",
        items: [
          { label: "Preferences", href: "#", icon: <Settings className="h-4 w-4" /> },
        ],
      },
    ],
  },
];

function SidebarDemo() {
  const [collapsed, setCollapsed] = React.useState(false);

  return (
    <SidebarLayout
      sidebar={
        <Sidebar
          position="left"
          width={260}
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          searchable={false}
          logo={<Logo size={28} />}
          header={
            <Text as="div" className="text-h6 font-semibold">
              Dream Light
            </Text>
          }
          footer={
            <Text as="div" className="text-xs text-muted-foreground">
              v2.0.0
            </Text>
          }
          navigation={demoNavigation}
        />
      }
    >
      <div className="grid grid-rows-[auto,1fr] h-full overflow-auto">
        <PageHeader
          title="Documentation"
          logo={<Logo size={32} title="Logo" />}
          onBack={() => window.history.back()}
        />
        <ScrollArea>
          <div className="px-6 py-8">
            <Text>Main content goes here</Text>
          </div>
        </ScrollArea>
      </div>
    </SidebarLayout>
  );
}

export default function DocsComponentsSidebar() {
  const code = `import { Sidebar, SidebarLayout } from "@/registry/new-york/blocks/sidebar/sidebar";
import { Book, FileText, Settings } from "lucide-react";

const navigation = [
  {
    title: "Documentation",
    subsections: [
      {
        title: "Getting Started",
        items: [
          { label: "Introduction", href: "#", icon: <Book className="h-4 w-4" /> },
          { label: "Installation", href: "#", icon: <FileText className="h-4 w-4" /> },
        ],
      },
    ],
  },
];

export function SidebarDemo() {
  const [collapsed, setCollapsed] = React.useState(false);

  return (
    <SidebarLayout
      sidebar={
        <Sidebar
          position="left"
          width={260}
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          logo={<Logo />}
          header={<Text className="font-semibold">Dream Light</Text>}
          navigation={navigation}
        />
      }
    >
      <main>Your content here</main>
    </SidebarLayout>
  );
}`;

  const installCommand = `npx shadcn@latest add @bluecopa-ui/sidebar`;

  return (
    <ComponentPage
      title="Sidebar"
      description="Full sidebar component with collapsible navigation, search, and customizable sections."
      preview={
        <div className="h-[500px] w-full border rounded-md overflow-hidden">
          <SidebarDemo />
        </div>
      }
      code={code}
      installCommand={installCommand}
      apiReference={{
        name: "SidebarProps",
        props: [
          {
            name: "position",
            type: '"left" | "right"',
            description: "Sidebar position. Defaults to 'left'.",
          },
          {
            name: "width",
            type: "number | string",
            description: "Fixed width in pixels or any CSS size (e.g., 280, '18rem').",
          },
          {
            name: "defaultCollapsed",
            type: "boolean",
            description: "Start collapsed.",
          },
          {
            name: "collapsed",
            type: "boolean",
            description: "Controlled collapsed state.",
          },
          {
            name: "onCollapsedChange",
            type: "(collapsed: boolean) => void",
            description: "Callback fired when collapsed state changes.",
          },
          {
            name: "header",
            type: "React.ReactNode",
            description: "Render header content.",
          },
          {
            name: "logo",
            type: "React.ReactNode",
            description: "Render logo.",
          },
          {
            name: "footer",
            type: "React.ReactNode",
            description: "Render footer content.",
          },
          {
            name: "navigation",
            type: "SidebarNavigationGroup[]",
            description: "Navigation groups with subsections and items.",
          },
          {
            name: "searchable",
            type: "boolean",
            description: "Enable search functionality.",
          },
        ],
      }}
    />
  );
}
